import type { TimberBuyerEditFormType } from '@schemas/edit.timberbuyer'
import { emptyToNull } from '@utils/string'
import type {
  CertificationType,
  CreateTimberBuyerRequest,
  CreateTimberBuyerResponse,
  DeliveryLocationModel,
  EmployeeModel,
  GetTimberBuyerDeliveryLocationsParams,
  GetTimberBuyerDeliveryLocationsResponse,
  GetTimberBuyersParams,
  GetTimberBuyersResponse,
  MainAssortmentModel,
  MeasurementAndTransportOrderIssuer,
  MeasurementOrganizationModel,
  SalesAgreementSummary,
  TimberBuyer,
  TimberBuyerStatus,
  TimberBuyerSummary,
  TransportPayerModel,
  TransportResponsibleModel,
} from '@/types/TimberBuyer'
import type { ContactPreferenceModel } from '@/types/TimberBuyers/ContactPreference'
import { ApiClient, ApiError, apiPost } from './ApiClient'

function createTimberBuyerUpdateBody(
  timberBuyer: TimberBuyer,
  updates: {
    status?: TimberBuyerStatus
    remark?: string
    contactPreferences?: ContactPreferenceModel[]
  },
) {
  const data = {
    tenantId: timberBuyer.tenantId,
    name: {
      ...timberBuyer.name,
      lastName: emptyToNull(timberBuyer.name.lastName),
    },
    status: updates.status ?? timberBuyer.status,
    customerNumber: timberBuyer.customerNumber,
    buyerNumber: timberBuyer.buyerNumber,
    organizationNumber: timberBuyer.organizationNumber,
    lineOfBusiness: timberBuyer.lineOfBusiness,
    remark: updates.remark ?? timberBuyer.remark,
    customerResponsible: timberBuyer.customerResponsible,
    contactInfo: timberBuyer.contactInfo,
    invoiceInformation: {
      ...timberBuyer.invoiceInformation,
      name: {
        ...timberBuyer.invoiceInformation.name,
        lastName: emptyToNull(timberBuyer.invoiceInformation.name.lastName),
      },
    },
    transportResponsible: timberBuyer.transportResponsible,
    transportPayer: timberBuyer.transportPayer,
    measurementOrganization: timberBuyer.measurementOrganization,
    contactPreferences: updates.contactPreferences ?? timberBuyer.contactPreferences,
  }
  return data
}

export async function getTimberBuyers(
  params: GetTimberBuyersParams,
): Promise<GetTimberBuyersResponse> {
  const queryParams: Record<string, string> = {}
  if (params.searchTerm) queryParams.searchTerm = params.searchTerm
  if (params.sort) queryParams.sort = params.sort

  const response = await ApiClient.get<TimberBuyerSummary[]>('/timber-buyers', {
    headers: {
      'x-tenant-id': params.tenantId,
      'x-page-size': params.pageSize?.toString() ?? '50',
      ...(params.continuationToken ? { 'x-continuation-token': params.continuationToken } : {}),
    },
    params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
  })

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const continuationToken = response.headers.get('x-continuation-token')
  const hasMoreResults = response.headers.get('x-has-more-results')?.trim().toLowerCase() === 'true'

  return {
    items: response.data,
    continuationToken: continuationToken ?? undefined,
    hasMore: hasMoreResults,
  }
}

export async function createTimberBuyer(
  data: CreateTimberBuyerRequest,
): Promise<CreateTimberBuyerResponse> {
  return apiPost<CreateTimberBuyerResponse>('/timber-buyers', data)
}

export async function getTimberBuyer(id: string): Promise<TimberBuyer> {
  const response = await ApiClient.get<TimberBuyer>(`/timber-buyers/${id}`)

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined

  return {
    ...response.data,
    etag,
  }
}

export async function getTimberBuyerDeliveryLocations(
  params: GetTimberBuyerDeliveryLocationsParams,
): Promise<GetTimberBuyerDeliveryLocationsResponse> {
  const response = await ApiClient.get<DeliveryLocationModel[]>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/delivery-locations`,
    {
      headers: {
        'x-tenant-id': params.tenantId,
      },
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return { items: response.data }
}

export type { MainAssortmentModel } from '@/types/TimberBuyer'

export interface CreateSalesAgreementBody {
  name: string
  validFrom: string
  validTo: string
  responsibleEmployee: EmployeeModel
  measurementAndTransportOrderIssuer: MeasurementAndTransportOrderIssuer
  certificationType: CertificationType
  certificateText?: string
  measurementCostsNotRefunded: boolean
  recipientOrderNumber?: string
  commission?: number
  vsysPricelistNumber?: string
  transportResponsible: TransportResponsibleModel
  transportPayer: TransportPayerModel
  measurementOrganization: MeasurementOrganizationModel
  deliveryLocations: DeliveryLocationModel[]
  mainAssortments: MainAssortmentModel[]
}

export async function createSalesAgreement(
  timberBuyerId: string,
  body: CreateSalesAgreementBody,
): Promise<SalesAgreementSummary> {
  const response = await ApiClient.post<SalesAgreementSummary>(
    `/timber-buyers/${encodeURIComponent(timberBuyerId)}/sales-agreements`,
    body,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function updateTimberBuyer(
  timberBuyer: TimberBuyer,
  updates: {
    status?: TimberBuyerStatus
    remark?: string
    contactPreferences?: ContactPreferenceModel[]
  },
): Promise<TimberBuyer> {
  const updateBody = createTimberBuyerUpdateBody(timberBuyer, updates)

  const response = await ApiClient.put<TimberBuyer>(
    `/timber-buyers/${timberBuyer.id}`,
    updateBody,
    {
      headers: timberBuyer.etag ? { 'If-Match': timberBuyer.etag } : undefined,
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined

  return {
    ...response.data,
    etag,
  }
}

export async function updateTimberBuyerRemark(
  timberBuyer: TimberBuyer,
  remark: string,
): Promise<TimberBuyer> {
  return updateTimberBuyer(timberBuyer, { remark })
}

export async function updateTimberBuyerStatus(
  timberBuyer: TimberBuyer,
  status: TimberBuyerStatus,
): Promise<TimberBuyer> {
  return updateTimberBuyer(timberBuyer, { status })
}

function getInvoiceRecipientNameFromForm(formData: TimberBuyerEditFormType): string {
  if (formData.invoiceRecipient === 'other') {
    return formData.invoiceRecipientName ?? ''
  }
  return formData.organizationName
}

function getContactInfoFromForm(formData: TimberBuyerEditFormType): TimberBuyer['contactInfo'] {
  if (!formData.email && !formData.phone) {
    return null
  }

  if (!formData.phone) {
    return { email: formData.email ?? null, number: null }
  }

  const phoneParts = formData.phone.trim().split(/\s+/)
  const [countryCode, number] = phoneParts.length === 2 ? phoneParts : [phoneParts[0], '']

  return {
    email: formData.email ?? null,
    number: { countryCode, number },
  }
}

export async function updateTimberBuyerGeneralInfo(
  timberBuyer: TimberBuyer,
  formData: TimberBuyerEditFormType,
): Promise<TimberBuyer> {
  const invoiceEmail =
    formData.invoiceDeliveryMethod === 'email' ? (formData.invoiceEmail ?? undefined) : undefined

  const updateBody = {
    tenantId: timberBuyer.tenantId,
    name: { firstName: formData.organizationName },
    status: timberBuyer.status,
    customerNumber: timberBuyer.customerNumber,
    buyerNumber: timberBuyer.buyerNumber,
    organizationNumber: timberBuyer.organizationNumber,
    lineOfBusiness: formData.lineOfBusiness,
    remark: timberBuyer.remark,
    customerResponsible: {
      employeeNumber: formData.customerResponsible?.number,
      name: formData.customerResponsible?.displayName,
      entraUserId: formData.customerResponsible?.id,
    },
    contactInfo: getContactInfoFromForm(formData),
    invoiceInformation: {
      name: { firstName: getInvoiceRecipientNameFromForm(formData) },
      email: invoiceEmail,
      mailAddress: {
        careOf: timberBuyer.invoiceInformation.mailAddress.careOf,
        name: timberBuyer.invoiceInformation.mailAddress.name,
        address: {
          streetName: formData.invoiceAddress,
          houseNumber: formData.invoiceHouseNumber,
          postalCode: formData.invoicePostCode,
          city: formData.invoiceCity,
          country: formData.invoiceCountry,
        },
      },
    },
    transportResponsible: formData.transportResponsible,
    transportPayer: formData.transportPayer,
    measurementOrganization: formData.measurementOrganization,
    contactPreferences: timberBuyer.contactPreferences,
  }

  const response = await ApiClient.put<TimberBuyer>(
    `/timber-buyers/${timberBuyer.id}`,
    updateBody,
    {
      headers: timberBuyer.etag ? { 'If-Match': timberBuyer.etag } : undefined,
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined

  return {
    ...response.data,
    etag,
  }
}

export async function updateTimberBuyerContactPreferences(
  timberBuyer: TimberBuyer,
  contactPreferences: ContactPreferenceModel[],
): Promise<TimberBuyer> {
  return updateTimberBuyer(timberBuyer, { contactPreferences })
}
